import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { INotification, INotificationsContext } from './Notifications.models';
import {
  NotificationContext,
  NotificationsContext,
  useNotificationsContext,
} from './NotificationsContext';
import { notificationsReducer } from './NotificationsReducer';
import { ErrorNotification } from './components/ErrorNotification';
import { NotificationTheme } from './components/GenericNotification';
import { SuccessNotification } from './components/SuccessNotification';

const NotificationItem = ({ notification }: { notification: INotification }) => {
  const notifications = useNotificationsContext();
  const { content: Content } = notification;

  const close = useCallback(() => {
    notifications.remove(notification.id);
  }, [notifications, notification.id]);

  const notificationProviderValue = useMemo(() => ({ close }), [close]);

  return (
    <NotificationContext.Provider value={notificationProviderValue}>
      <div className="justify-self-end pointer-events-auto">
        <Content />
      </div>
    </NotificationContext.Provider>
  );
};

const NotificationsWrapper = ({ notifications }: INotificationsContext) => {
  if (notifications.length) {
    return (
      <div className="grid gap-2 position: fixed top-4 right-4 z-[1500] pointer-events-none ">
        {notifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} />
        ))}
      </div>
    );
  }

  return null;
};

export const NotificationsProdiver = ({ children }) => {
  const [notifications, notificationsDispatch] = useReducer(notificationsReducer, []);
  const pendingTimeouts = useRef(new Set<NodeJS.Timeout>());

  // An auto-dismiss timer that outlives the provider dispatches into an unmounted tree.
  useEffect(() => {
    const timeouts = pendingTimeouts.current;

    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const add = useCallback(
    (content: () => React.JSX.Element, delay: number = 4000) => {
      const notificationId = Date.now();

      const notificationTimeout = setTimeout(() => {
        pendingTimeouts.current.delete(notificationTimeout);
        notificationsDispatch({ type: 'REMOVE', id: notificationId });
      }, delay);

      pendingTimeouts.current.add(notificationTimeout);

      notificationsDispatch({
        type: 'ADD',
        payload: { id: notificationId, timeout: notificationTimeout, content },
      });
    },
    [notificationsDispatch]
  );

  const success = useCallback(
    (
      params: { title: string; description: string; theme?: NotificationTheme },
      delay: number = 4000
    ) => {
      add(
        () => (
          <SuccessNotification
            title={params.title}
            description={params.description}
            theme={params.theme}
          />
        ),
        delay
      );
    },
    [add]
  );

  const error = useCallback(
    (
      params: { title: string; description: string; theme?: NotificationTheme },
      delay: number = 4000
    ) => {
      add(
        () => (
          <ErrorNotification
            title={params.title}
            description={params.description}
            theme={params.theme}
          />
        ),
        delay
      );
    },
    [add]
  );

  const remove = useCallback(
    (id: number) => {
      const notification = notifications.find((n) => n.id === id);

      if (notification) {
        clearTimeout(notification.timeout);
        pendingTimeouts.current.delete(notification.timeout);
      }

      notificationsDispatch({ type: 'REMOVE', id });
    },
    [notifications, notificationsDispatch]
  );

  const removeAll = useCallback(() => {
    notifications.forEach((notification) => {
      clearTimeout(notification.timeout);
      pendingTimeouts.current.delete(notification.timeout);
    });

    notificationsDispatch({ type: 'REMOVE_ALL' });
  }, [notifications, notificationsDispatch]);

  const notificationsProviderValue = useMemo(
    () => ({ notifications, add, success, error, remove, removeAll }),
    [notifications, add, success, error, remove, removeAll]
  );

  return (
    <NotificationsContext.Provider value={notificationsProviderValue}>
      {children}
      {createPortal(
        <NotificationsWrapper
          notifications={notifications}
          add={add}
          error={error}
          success={success}
          remove={remove}
          removeAll={removeAll}
        />,
        document.body
      )}
    </NotificationsContext.Provider>
  );
};
